'use client'

import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import type { TileLife } from './liveStatus'
import LightningIcon from 'src/app/assets/icons/ico-bolt-lightning.svg'
import { WinPill, type WinTier } from 'src/components/ChipPlacementPanel/WinPill'
import { chipPileLayers, type ChipStyle } from './chipPalette'
import { PriceReel } from './PriceReel'
import { PuttGlyph } from './PuttGlyph'
import { RoundMark } from './RoundMark'
import { BALL_SIZE_PX, ROUND_MARK_PX, type BirdieTileView } from './tileViewModel'

export interface BirdieTileProps {
  view: BirdieTileView
  /**
   * Which side of the tile the settled <WinPill> sits on (desktop board only —
   * owner, 2026-09-08: the pill beside the option with an arrow pointing at it,
   * right of a count bar, left or right of an exact tile by its grid column).
   * Unset on the phone's stack, where the pill keeps riding the top edge.
   */
  pillSide?: 'left' | 'right'
  placedAmount?: number
  placedCount?: number
  /**
   * Set when the stake on this option is a free bet (B38). The chip is then a
   * gold ticket carrying the reward's word over its amount rather than a chip
   * from the ladder — the shared panel's own treatment
   * (`ChipPlacementPanel.tsx:3088-3120`), which bypasses the chip colours
   * entirely because the read is "this is free", not "this is a $25 stake".
   * A free bet never stacks, so the pile behind it is never drawn.
   */
  freeBet?: boolean
  /** The localised word on that ticket. The host owns it — `t` lives there. */
  freeBetLabel?: string
  /**
   * The ladder colours the placed TOTAL is drawn in (B20 Task 2 item 4). The
   * host owns it because the ladder is painted onto the LIVE chip values, which
   * are config- and balance-driven; the tile only spends it.
   */
  chipStyle?: ChipStyle
  locked: boolean
  /** B34 live ticket status: dead once the putts on screen rule the option out. */
  life?: TileLife | undefined
  /** B34: the big alive option while the third putt is under way. */
  hot?: boolean
  /**
   * B40: this option was ONE BALL from paying when the third rimmed out
   * (`liveStatus.nearMissIds`). It dies like the rest — the result is the result
   * — but it is allowed to be seen going.
   */
  nearMiss?: boolean
  /**
   * B32: an armed auto-bet zone passed this tile over. The ghost is opacity
   * and saturation only — the tile keeps its box, its price and its tap, since
   * a hand on it is what turns the rule off. Off by default, so a board with
   * no rule renders exactly what it rendered before B32.
   */
  zoneMiss?: boolean
  receiving: boolean
  winner: boolean
  myBetWon: boolean
  myBetLost: boolean
  winPayout?: number
  winTier?: WinTier
  winDelayMs?: number
  currencySymbol: string
  /** Index in OPTIONS order — staggers the lightning sheen, like `--shine-delay`. */
  shineIndex: number
  /**
   * B40: a value that changes when the board is priced for a new round — the
   * race id. The tiles keep their DOM nodes across rounds (they are keyed by
   * option id, and an option id is the market's, not the round's), so an
   * arrival cascade has nothing to hang a CSS one-shot on without it. Absent =
   * no cascade, which is what every existing caller and every test gets.
   */
  enterKey?: string
  /** B40: this tile's place in the arrival cascade, ms (`boardMotion`). */
  enterDelayMs?: number
  /**
   * B40: this tile's place in the DIM wave, ms — out of the middle while the
   * putts play, out of the winner once the round settles. Spent by the tile's
   * own opacity/filter transitions, never by an animation.
   */
  waveDelayMs?: number
  /**
   * B40: how many putts are on screen, 0-3. The alive tiles answer each one with
   * a single pulse, so the board keeps time with the stream. It is the COUNT and
   * not a boolean because the pulse has to restart on every putt, and the only
   * way to restart a CSS one-shot from a prop is to change which rule matches —
   * the count's parity picks between two identical keyframes.
   */
  beat?: number
  style?: CSSProperties
  chipLabel: (amount: number) => string
  /**
   * The localised "…placed" (and, after settlement, won/lost) sentence for a
   * tile that carries a stake — B11 item 9. The host owns it because `t` lives
   * there; the tile only decides whether there is anything to describe.
   */
  stakeDescription?: (input: {
    amount: number
    won: boolean
    lost: boolean
    free?: boolean
  }) => string
  onPlace: (optionId: string, target: DOMRect) => void
  onRemove: (optionId: string) => void
  /**
   * B28: what a LOCKED tile does when it is tapped. Optional because the tile
   * is B3's shipped contract and a host that has nothing to say may leave it
   * off — the tile still refuses the placement either way.
   *
   * It exists at all because the answer is the host's to give: the sentence is
   * localised, and `t` lives on the board, not here.
   */
  onLockedPress?: (optionId: string) => void
}

/** How long the shake runs, ms — `.birdie-tile--nudge`'s own `0.3s`. */
const NUDGE_MS = 300

/**
 * B40 — how long `.birdie-tile--enter` stays on, ms. Comfortably past the
 * longest delay the cascade hands out (`TILE_CASCADE_MAX_MS`) plus the
 * animation's own 0.34 s, because the class is dropped on a TIMER rather than on
 * the animation's end: under `prefers-reduced-motion` the animation is collapsed
 * to `none` and no end event is ever dispatched — the same trap `--nudge`
 * documents above.
 */
const ENTER_MS = 900

/**
 * Polish-bar guarantees that must hold whatever the stylesheet does, because
 * `birdie-board.scss` positions tiles on boxes computed by `boardGeometry`:
 *  - the root paints its chrome INSIDE its own width/height (border-box, inset
 *    shadows only — never a margin or an outset shadow that would move the box);
 *  - the price is the hero and never wraps or re-flows between 2.53x and
 *    35.18x (tabular figures).
 */
const ROOT_STYLE: CSSProperties = { boxSizing: 'border-box' }
const NUMERIC_STYLE: CSSProperties = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }

/**
 * One option. B20 stripped it to the two things it has to say and nothing else:
 * the BALLS and the PRICE.
 *
 * Owner (B20 sign-off): no text on any tile. The localised label row is gone —
 * an EXACT tile's three ordered balls already spell "hole, miss, hole", and a
 * count bar's balls are its answer ("two balls in, in any order"), so the words
 * were restating the picture in sixteen languages inside a 74-px box. The two
 * COLUMN HEADERS are now the board's only text.
 *
 * The name is not gone, only unseen: `aria-labelledby` points at a
 * `birdie-sr-only` span carrying `view.ariaLabel`, so a screen-reader player
 * still hears "Hole Miss Hole, pays 15.07x". It is `aria-labelledby` rather than
 * `aria-label` for a specific reason — the stake sentence lives in a second
 * hidden span INSIDE the button, and with a content-derived name it would be
 * concatenated into the name and then announced again as the description.
 * Naming the one span settles which is which.
 *
 * The two families differ in SHAPE, not in vocabulary: the exact tile is a
 * centred stack (balls over price), the count bar a row (balls left, price
 * right). Both draw the same ball at the same ring geometry.
 *
 * The glyph row is the flying chip's landing target — the tile hands its rect to
 * the caller (the CrashBetZone pattern, CrashBetZone.tsx:365) instead of the
 * board reading a ref map and hard-coding an offset the way the shared panel
 * does (ChipPlacementPanel.tsx:1414-1415).
 *
 * Every state is a distinct BEM modifier on the root so the SCSS has one hook
 * per state and this component keeps no styling opinions of its own.
 */
export function BirdieTile({
  view,
  life,
  hot = false,
  nearMiss = false,
  zoneMiss = false,
  placedAmount,
  placedCount = 0,
  freeBet = false,
  freeBetLabel,
  chipStyle,
  locked,
  receiving,
  winner,
  myBetWon,
  myBetLost,
  winPayout = 0,
  winTier = 'win',
  winDelayMs = 0,
  pillSide,
  currencySymbol,
  shineIndex,
  enterKey,
  enterDelayMs = 0,
  waveDelayMs = 0,
  beat = 0,
  style,
  chipLabel,
  stakeDescription,
  onPlace,
  onRemove,
  onLockedPress
}: BirdieTileProps) {
  const glyphRowRef = useRef<HTMLSpanElement>(null)
  const uid = useId()
  const nameId = `${uid}-name`
  const descriptionId = `${uid}-stake`

  // B28: the one-shot shake a locked tile answers a tap with. Dropped, then
  // re-raised, so a SECOND tap replays the animation instead of finding the
  // class already on the element and doing nothing.
  const [nudging, setNudging] = useState(false)
  const nudgeFrame = useRef(0)
  const nudgeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Nothing outlives the tile: a tap on the last frame before this option stops
  // being rendered would otherwise land on an unmounted node.
  useEffect(
    () => () => {
      if (nudgeFrame.current) cancelAnimationFrame(nudgeFrame.current)
      if (nudgeTimer.current) clearTimeout(nudgeTimer.current)
    },
    []
  )
  const startNudge = useCallback(() => {
    if (nudgeFrame.current) cancelAnimationFrame(nudgeFrame.current)
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current)
    setNudging(false)
    // TWO frames, and the reason is subtle enough to be worth the line. A
    // callback registered from a click handler runs at the START of the next
    // frame's rendering steps — BEFORE the style recalculation that would have
    // processed the dropped class — so dropping and re-raising inside one frame
    // is a pair the engine coalesces, the running animation is simply retained,
    // and a second tap inside the 300 ms window is answered with nothing. The
    // board's notice gets away with a single frame because it drops to null and
    // UNMOUNTS its element, and a fresh node always animates; this is the same
    // button throughout. So: one frame to let the removal land, the next to
    // re-raise.
    nudgeFrame.current = requestAnimationFrame(() => {
      nudgeFrame.current = requestAnimationFrame(() => {
        nudgeFrame.current = 0
        setNudging(true)
        // Cleared on a TIMER rather than on the animation's end. Under
        // `prefers-reduced-motion` the shake is collapsed to `animation: none`,
        // no end event is ever dispatched, and the class used to stay on the
        // button for the life of the tile — invisible only because the modifier
        // declares nothing but the animation, and a trap the day it declares
        // anything else.
        nudgeTimer.current = setTimeout(() => {
          nudgeTimer.current = null
          setNudging(false)
        }, NUDGE_MS)
      })
    })
  }, [])

  // B40: the arrival cascade. One shot per round, dropped on a timer for the
  // same reason the shake is (see `ENTER_MS`). Nothing runs when the host passes
  // no `enterKey`, which is every caller that predates B40.
  const [entering, setEntering] = useState(false)
  const enteredKey = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (enterKey === undefined || enteredKey.current === enterKey) return undefined
    enteredKey.current = enterKey
    setEntering(true)
    const timer = setTimeout(() => {
      setEntering(false)
    }, ENTER_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [enterKey])

  const handleClick = useCallback(() => {
    // An option this round did not price stays inert in every sense — it is
    // still natively disabled, so this is belt and braces.
    if (view.disabled) return
    if (locked) {
      startNudge()
      if (onLockedPress) onLockedPress(view.optionId)
      return
    }
    const target = glyphRowRef.current?.getBoundingClientRect()
    if (target) onPlace(view.optionId, target)
  }, [locked, onLockedPress, onPlace, startNudge, view.disabled, view.optionId])

  /**
   * B40 — THE FOCUS PULL, AND THE ONE LINE THAT KEEPS THE OWNER'S CONSTRAINT.
   *
   * While the putts play the board gives the stage back to the video by letting
   * the options the player has NO money on sit back a little. Three exclusions,
   * all of them deliberate:
   *  - `placedAmount === undefined` — a tile the player is backing is never
   *    receded, never scaled, never faded. Their money stays the most legible
   *    thing on the board (owner, approving B40: "just important the betting
   *    options are still visible and the players bets").
   *  - `!hot` — the third-putt option is the one thing the board is asking to be
   *    looked at, so it cannot also be the thing stepping back.
   *  - `!winner` — belt and braces. `boardPhase` empties `livePutts` on the first
   *    settled frame, so `life` is already undefined by then.
   */
  const recede = life !== undefined && placedAmount === undefined && !hot && !winner

  /**
   * B40: the alive tiles answer each putt with one pulse. Two class names, two
   * identical keyframes, picked by the putt count's parity — changing which rule
   * matches is what restarts a one-shot on an element that never remounts.
   */
  // Written as a statement rather than a nested ternary, and never as an index
  // into a lookup: a `beatClass` that could come back `undefined` would reach the
  // class list, and an "undefined" class on a tile is a trap this stylesheet has
  // paid for before.
  let beatClass = ''
  if (beat > 0 && life === 'alive') {
    beatClass = beat % 2 === 1 ? 'birdie-tile--beat-a' : 'birdie-tile--beat-b'
  }

  const className = [
    'birdie-tile',
    `birdie-tile--${view.kind}`,
    // B36: the round-type family has two members that must NOT look alike — ice and ember are the
    // whole tell. The key is the only thing that separates them, so it becomes a class.
    view.kind === 'round' ? `birdie-tile--${view.key.toLowerCase()}` : '',
    placedAmount !== undefined ? 'birdie-tile--placed' : '',
    // The absence of a stake, as a class. A count bar's settled pill is offset
    // 32 px from the right edge to clear the corner chip; with no chip there is
    // nothing to clear, and the mockup brings the tag back to the corner
    // (`.count .wp.nochip`). The stylesheet cannot ask a tile whether one of
    // its children exists at this target, so the tile says so itself.
    placedAmount === undefined ? 'birdie-tile--unbacked' : '',
    locked ? 'birdie-tile--locked' : '',
    life === 'dead' ? 'birdie-tile--dead' : '',
    life === 'alive' && placedAmount !== undefined ? 'birdie-tile--alive-mine' : '',
    hot ? 'birdie-tile--hot' : '',
    // Only where it is also DEAD, which is the only state it can be in — the
    // third ball has landed. Two classes deep in the stylesheet, so a near miss
    // can never be mistaken for a tile that is still in play.
    nearMiss && life === 'dead' ? 'birdie-tile--near-miss' : '',
    recede ? 'birdie-tile--recede' : '',
    // B40: the board's RESPONSE escalates with the win even where the pill's own
    // scale cannot. Birdie pins `--tier-scale: 1` because a 42-px bar has no room
    // for the shared panel's escalation, so a 118x hit and a 2.15x hit landed
    // identically; the shockwave, its bloom and the pill's drop now take the
    // tier instead. Only on a tile the PLAYER backed — a tier is a multiple of
    // their stake, and a winner nobody was on has no tier to escalate.
    myBetWon ? `birdie-tile--win-${winTier}` : '',
    beatClass,
    entering ? 'birdie-tile--enter' : '',
    zoneMiss ? 'birdie-tile--zone-miss' : '',
    nudging ? 'birdie-tile--nudge' : '',
    view.disabled ? 'birdie-tile--disabled' : '',
    view.boosted ? 'birdie-tile--lightning' : '',
    receiving ? 'birdie-tile--receiving' : '',
    winner ? 'birdie-tile--winner' : '',
    myBetWon ? 'birdie-tile--my-win' : '',
    myBetLost ? 'birdie-tile--my-loss' : '',
    pillSide !== undefined ? `birdie-tile--pill-${pillSide}` : ''
  ]
    .filter(Boolean)
    .join(' ')

  const shadowLayers = chipPileLayers(placedCount)
  // What the tile REPORTS, which since B28 is no longer what the DOM enforces:
  // a locked tile is unavailable to a screen-reader player and to the click
  // handler, but it is not natively disabled, so the tap still arrives.
  const inert = locked || view.disabled
  // Positional ids, not the map index: the glyph row is putt 1-2-3 and never
  // reorders, so the putt number is the honest identity (and keeps
  // react/no-array-index-key quiet).
  const glyphSlots = view.glyphs.map((mark, index) => ({ id: `${view.key}-p${index + 1}`, mark }))

  // B11 item 9 / review 11-12: `aria-pressed` gave a stake ACCUMULATOR toggle
  // semantics — a screen reader announced "pressed" on a tile the player had
  // put four chips on, and "not pressed" after a Clear, neither of which is
  // what the control does. The stake is a DESCRIPTION of the button, so it
  // moves to `aria-describedby`, which also lets the amount (and the settled
  // outcome) be announced at all — the English `aria-label` had hidden both.
  const description =
    placedAmount !== undefined && stakeDescription
      ? stakeDescription({
          amount: placedAmount,
          won: myBetWon,
          lost: myBetLost,
          ...(freeBet ? { free: true } : {})
        })
      : ''

  // The ladder colours reach the chips as custom properties rather than as
  // three inline declarations per layer: one place to read, and the shadow
  // layers inherit the same three values the front chip uses.
  // B38: the price, and whether a claimed booster is what raised it.
  const boosterMark = view.boosterPercent > 0
  // B40: the figure counts into place on per-digit reels (`PriceRoll`). It is
  // `aria-hidden` because the price is already in the tile's accessible name —
  // the reels carry ten digits of text each, and a screen reader left to read
  // them would read the reel rather than the price.
  const price = (
    <span aria-hidden="true" className="birdie-tile__pays-value" style={NUMERIC_STYLE}>
      <PriceReel text={view.multiplierText} />
    </span>
  )

  const chipVars = chipStyle
    ? ({
        '--birdie-chip-bg': chipStyle.bg,
        '--birdie-chip-border': chipStyle.border,
        '--birdie-chip-color': chipStyle.color
      } as CSSProperties)
    : undefined

  return (
    <button
      aria-describedby={description ? descriptionId : undefined}
      aria-disabled={inert}
      aria-labelledby={nameId}
      className={className}
      // B28: `view.disabled` ALONE. A natively disabled control dispatches no
      // click at all and the event does not bubble, so a locked tile could
      // never be told anything — not even "not now". `aria-disabled` above
      // keeps reporting both, so the tile stays focusable and still announces
      // itself as unavailable, and can now say why.
      disabled={view.disabled}
      onClick={handleClick}
      style={
        {
          ...style,
          ...ROOT_STYLE,
          '--shine-delay': `${shineIndex * 0.12}s`,
          // B40: the two board-wide beats, as this tile's own place in them
          // (`boardMotion`). Both are plain delays spent by the stylesheet — the
          // tile never reads a clock and never measures anything.
          '--birdie-enter-delay': `${enterDelayMs}ms`,
          '--birdie-wave-delay': `${waveDelayMs}ms`
        } as CSSProperties
      }
      type="button"
    >
      {/*
        B40 — the tile's MATERIAL, as one layer under everything else.
        A specular top edge, an inner shadow and a contact shadow below the box,
        so the tile sits ON the green instead of floating over it. It is its own
        element rather than the tile's `::before` because the two round-type
        tiles already spend both of their pseudo-elements on the weather, and
        because the rim flash a landing chip answers with rides it.

        It carries NO content and is `z-index: 0` under content at `z-index: 1`,
        which is the promise the whole board is built on: an effect sits behind
        or around what the player reads, never over it.
      */}
      <span aria-hidden="true" className="birdie-tile__bevel" />

      {/*
        B40: the settled winner's shockwave — a gold ring out of the tile, once.
        Rendered only in the winning state, like the lightning seal, so there is
        nothing to animate on any other tile and nothing left over afterwards.
      */}
      {winner ? <span aria-hidden="true" className="birdie-tile__burst" /> : null}

      {/*
        The lightning seal and its sheen (B20 Task 2 item 3). The seal is the
        platform's own bolt in a gold disc on the TOP-LEFT corner; the chip pile
        hangs off the top-right, so the two never meet whatever the stake is.
        The sheen is a static clipping layer — `inset: 0; overflow: hidden` —
        whose child sweeps ONCE when the class lands and then rests. Both are
        rendered only in the boosted state, so the sweep starts on the frame the
        boost becomes visible and nothing animates on an ordinary tile.
      */}
      {view.boosted ? (
        <>
          <span aria-hidden="true" className="birdie-tile__sheen" />
          <span aria-hidden="true" className="birdie-tile__seal">
            <LightningIcon />
          </span>
        </>
      ) : null}

      {winner ? (
        <WinPill
          currencySymbol={currencySymbol}
          delayMs={winDelayMs}
          isPayout={myBetWon}
          payoutAmount={winPayout}
          tier={winTier}
        />
      ) : null}

      {/*
        The balls. An EXACT tile draws all three putts in order — the order IS
        the bet. A COUNT bar draws only what it claims: IN1..IN3 that many holed
        balls, left-aligned, and IN0 the three missed ones, because "none" cannot
        be drawn as an absence of balls (`glyphsForKey`, B13).
      */}
      <span className="birdie-tile__glyphs" ref={glyphRowRef}>
        {view.kind === 'round' ? (
          // B36: a round-type tile draws no putts, because it describes none — it is settled by the
          // round's own seed, not by what the golfer does. The mark is the whole tell.
          <RoundMark kind={view.key === 'FIRE' ? 'FIRE' : 'FROST'} size={ROUND_MARK_PX} />
        ) : (
          glyphSlots.map((slot) => (
            <PuttGlyph key={slot.id} mark={slot.mark} size={BALL_SIZE_PX[view.kind]} />
          ))
        )}
      </span>

      {/*
        B11 item 2: a lightning tile's hero and its struck-through base price
        shared one row, which overflowed and clipped `142.50x` to `42.50x` — a
        boosted price that read as LOWER than the base. The base gets its own
        line under the hero whenever it exists, at half the hero's size — and
        the hero keeps its family's size either way (B20 review): both lines
        fit, and a boosted price drawn smaller than the plain one beside it
        would undercut the boost it is announcing.
      */}
      {/*
        B38: an inventory booster is already folded into `multiplierText` — the
        shared panel folds it into the same number too
        (`ChipPlacementPanel.tsx:3045-3055`). The bolt beside it is the soft
        secondary mark saying WHY this price is higher than the tile next to it,
        the same glyph the panel puts there, and deliberately NOT the round's own
        lightning seal: that is a different fact and lives on the top-left corner.

        It sits in a row of its own rather than inside `__pays-value`, whose
        gradient is painted with `background-clip: text` and a transparent fill —
        an SVG in there would be clipped away with the rest of the box.
      */}
      <span className={`birdie-tile__pays${boosterMark ? ' birdie-tile__pays--booster' : ''}`}>
        {boosterMark ? (
          <span className="birdie-tile__pays-head">
            {price}
            <LightningIcon aria-hidden="true" className="birdie-tile__pays-booster-ico" />
          </span>
        ) : (
          price
        )}
        {view.baseMultiplierText ? (
          <span className="birdie-tile__pays-base" style={NUMERIC_STYLE}>
            {view.baseMultiplierText}
          </span>
        ) : null}
      </span>

      {placedAmount !== undefined && freeBet ? (
        /*
          B38: a free bet is not a stake off the ladder, so it is not drawn as
          one. No pile behind it — one inventory item cannot stack — no ladder
          colours, and the reward's own word over the amount, which is the shared
          panel's composition at this board's scale
          (`ChipPlacementPanel.tsx:3096-3122`). The ✕ still removes it, which
          releases the item back into the pool for another option.
        */
        <span className="birdie-tile__chip-stack birdie-tile__chip-stack--freebet">
          <span className="birdie-tile__chip birdie-tile__chip--top birdie-tile__chip--freebet">
            <span className="birdie-tile__chip-free">{freeBetLabel}</span>
            <span className="birdie-tile__chip-amount" style={NUMERIC_STYLE}>
              {chipLabel(placedAmount)}
            </span>
            {!locked ? (
              <span
                className="birdie-tile__chip-x"
                onClick={(event) => {
                  event.stopPropagation()
                  onRemove(view.optionId)
                }}
                role="presentation"
              >
                ✕
              </span>
            ) : null}
          </span>
        </span>
      ) : null}

      {placedAmount !== undefined && !freeBet ? (
        <span className="birdie-tile__chip-stack" style={chipVars}>
          {/*
            Deepest layer FIRST, exactly as the shared panel emits them
            (`ChipPlacementPanel.tsx:3152-3155`): every layer is absolute at the
            same z-index, so DOM order is paint order and a 1-2-3 sequence would
            put the faintest, furthest chip ON TOP of the nearest one. Depth is
            1-based — at 0 the transform, the opacity and the brightness are all
            identities, i.e. a shadow chip hidden exactly behind the front one.
          */}
          {Array.from({ length: shadowLayers }, (_, index) => (
            <span
              className="birdie-tile__chip birdie-tile__chip--shadow"
              key={`shadow-${index}`}
              style={{ '--stack-depth': shadowLayers - index } as CSSProperties}
            />
          ))}
          <span className="birdie-tile__chip birdie-tile__chip--top">
            <span className="birdie-tile__chip-amount" style={NUMERIC_STYLE}>
              {chipLabel(placedAmount)}
            </span>
            {!locked ? (
              <span
                className="birdie-tile__chip-x"
                onClick={(event) => {
                  event.stopPropagation()
                  onRemove(view.optionId)
                }}
                role="presentation"
              >
                ✕
              </span>
            ) : null}
          </span>
        </span>
      ) : null}

      <span className="birdie-sr-only" id={nameId}>
        {view.ariaLabel}
      </span>

      {description ? (
        <span className="birdie-sr-only" id={descriptionId}>
          {description}
        </span>
      ) : null}
    </button>
  )
}
